<template>
  <div class="content" id="page--quote-margin-setting">
    <div class="header">
      <h1>{{ pageTitle }}</h1>
      <Button style="float: right;" icon="ios-git-network" type="default" @click="showWorkflowStatus = true">{{
          $t('menu.quote.configure_workflow')
      }}</Button>
    </div>
    <Spin v-if="loadingQuote"></Spin>
    <div v-else class="content-body table-wrapper">
      <div id="quote-information" class="block" v-if="quote">
        <div class="ivu-row">
          <Col span="3" class="title">{{ $t('quote.info.type') }}</Col>
          <Col span="5" class="value">{{ quote.type }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.customer') }}</Col>
          <Col span="5" class="value">{{ quote.customer ? quote.customer.name : 'N/A' }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.created') }}</Col>
          <Col span="5" class="value">{{ quote.created_at }}</Col>
        </div>
        <div class="ivu-row">
          <Col span="3" class="title">{{ $t('quote.info.status') }}</Col>
          <Col span="5" class="value">{{ quote.status }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.currency') }}</Col>
          <Col span="5" class="value">{{ quote.currency }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.exchange_rate') }}</Col>
          <Col span="5" class="value">{{ quote.exchange_rate }}</Col>
        </div>
        <div class="ivu-row" v-if="quote.type === 'single_product'">
          <Col span="3" class="title">{{ $t('quote.info.product') }}</Col>
          <Col span="5" class="value">{{ productName ? productName : 'N/A' }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.quantity') }}</Col>
          <Col span="5" class="value">{{ quote.data ? quote.data.quantity : 'N/A' }}</Col>
          <Col span="3" class="title">{{ $t('quote.info.expected_delivery_date') }}</Col>
          <Col span="5" class="value">{{ quote.data ? quote.data.expected_delivery_date : 'N/A' }}</Col>
        </div>
      </div>

      <div class="block margin-top-20">
        <h2>{{ $t('quote.margin_setting.cost_breakdown') }}</h2>
        <div id="costs-breakdown">
          <Collapse value="additionalExpenses">
            <Panel>
              <p slot="content">
                <MarginSettingSummaryPivot v-if="costEstimatorId" :cost-estimator-id="costEstimatorId">
                </MarginSettingSummaryPivot>
              </p>
            </Panel>
          </Collapse>
        </div>
      </div>

      <MarginSettingForm v-if="quote && quote.data && costEstimatorId" ref="marginSettingFormRef" :quoteId="quoteId"
        :quantity="quote.data.quantity" :currencyDefault="quote.currency" :exchangeRate="quote.exchange_rate"
        :additional-cost-params="costAdditionalParams" :cost-estimator-id="costEstimatorId"
        :default-setting="marginSetting">
      </MarginSettingForm>
    </div>
    <Drawer v-model="showWorkflowStatus" title="Workflow status" width="420" :mask-closable="false">
      <WorkflowStatePipelineWidget workflow-name="quote" model-type="quote" :display-edit-button="false"
        :display="workflowDisplay" :model-id="this.quoteId">
      </WorkflowStatePipelineWidget>
    </Drawer>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import MarginSettingForm from './components/forms/margin-setting-form'
import MarginSettingSummaryPivot from './components/widgets/margin-setting-summary-pivot'
import { intlCurrencyNumber } from '@/libs/util'
import WorkflowStatePipelineWidget from './components/widgets/workflow-state-pipeline-widget'
import WORKFLOW_CONFIG from '@/view/quote/data/config'
import CONFIG from '@/config'

export default {
  name: 'QuoteMarginSetting',
  components: {
    MarginSettingForm,
    MarginSettingSummaryPivot,
    WorkflowStatePipelineWidget
  },
  data() {
    return {
      quote: null,
      costAdditionalParams: { qc_cost: 0, company_expenses: 0 },
      marginSetting: {},
      loadingQuote: false,
      loadingCostsSummary: false,
      costEstimatorId: null,
      product: null,
      workflowDisplay: WORKFLOW_CONFIG.workflow.display,
      showWorkflowStatus: false
    }
  },
  computed: {
    quoteId: function () {
      return this.$route.params.quoteId
    },
    productName: function () {
      return this.product?.product_name
    },
    pageTitle: function () {
      document.title = this.$t('menu.quote.margin_setting.title') + ' - ' + this.quote?.title
      return document.title
    }
  },
  mounted() {
    this.getQuote(this.quoteId)
    this.getCostsData(this.quoteId)
  },
  methods: {
    ...mapActions([
      'loadQuote',
      'getCosts',
      'saveCosts',
      'getProduct'
    ]),
    intlCurrencyNumber,
    getQuote: function (quoteId) {
      this.loadingQuote = true
      this.loadQuote(quoteId).then(resp => {
        this.quote = resp.data
        this.quote.exchange_rate = this.quote.exchange_rate ? _.round(resp.data.exchange_rate, 5) : CONFIG.defaultChangeRate
        this.marginSetting.tax_rate = this.quote.tax_rate

        if (this.quote.data && this.quote.data.product_id) {
          this.getProduct(this.quote.data.product_id).then(resp => {
            this.product = resp
          })
        }
      }).finally(() => {
        this.loadingQuote = false
      })
    },
    getCostsData(quoteId) {
      this.getCosts(quoteId).then(resp => {
        this.costAdditionalParams.qc_cost = resp.data.qc_cost || 0
        this.costAdditionalParams.company_expenses = resp.data.company_expenses || 0
        this.costEstimatorId = resp.data.cost_estimator_id
      })
    }
  },
  watch: {
    '$route.params.quoteId': function (newVal) {
      console.debug(newVal)
      if (newVal) {
        this.quoteId = newVal
        this.getQuote(newVal)
        this.getCostsData(newVal)
      }
    }
  }
}
</script>

<style lang="less">
#page--quote-margin-setting {
  .title.ivu-col {
    background-color: #f3f1f1;
    border-bottom: 1px solid #fff;
  }

  .summary-preview {
    padding: 0 15px;
  }
}
</style>
